@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.schulung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginekalender.EngineMonthCalendar
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.GlobalManager
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.js.Promise
import kotlin.coroutines.resume

private val schulungsKopfHeaders = listOf(
    "ID",
    "Schulungsname",
    "Modus",
    "Typ",
    "Beschreibung",
    "Anzahl Teilnehmer",
    "Absolviert",
    "Datum",
    "Uhrzeit von",
    "Uhrzeit bis",
    "Standort",
    "Aktiv von",
    "Aktiv bis"
)

private val schulungsTeilnehmerHeaders = listOf(
    "Pflicht",
    "Benutzer-ID",
    "Mitarbeiter",
    "Personalnummer",
    "Start Schulung",
    "Ende Schulung",
    "Teilgenommen",
    "Zustand"
)

@Composable
fun Schulungsverwaltung() {
    SchulungsSplitScreen(readOnly = false)
}

@Composable
fun SchulungsAuskunft() {
    SchulungsSplitScreen(readOnly = true)
}

@Composable
private fun SchulungsSplitScreen(readOnly: Boolean) {
    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        GlobalManager.jsonDomainVersion("schulung"),
        GlobalManager.jsonDomainVersion("kalender"),
        GlobalManager.jsonDomainVersion("user")
    )
    var schulungen by remember { mutableStateOf<List<All>>(emptyList()) }
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var teilnehmer by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedSchulungId by remember { mutableStateOf<String?>(null) }
    var schulungsFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var teilnehmerFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var createDialogOpen by remember { mutableStateOf(false) }
    var editDialogOpen by remember { mutableStateOf(false) }
    var settingsDialogOpen by remember { mutableStateOf(false) }
    var inhaltPopupSchulung by remember { mutableStateOf<All?>(null) }

    suspend fun reloadSchulungen() {
        schulungen = schulungsFilter?.let { SchulungApi.searchSchulungen(it) }
            .orEmpty().sortedBy { it.id.toIntOrNull() ?: 0 }
        users = SchulungApi.listUsers().sortedBy { listOf(it.nachname, it.vorname, it.id).joinToString("|") }
    }

    suspend fun reloadTeilnehmer() {
        teilnehmer = teilnehmerFilter?.let { filters -> selectedSchulungId
            ?.takeIf { it.isNotBlank() }
            ?.let { SchulungApi.searchTeilnehmer(it, filters) }
        }.orEmpty()
    }

    LaunchedEffect(refreshVersion) {
        reloadSchulungen()
        reloadTeilnehmer()
    }

    LaunchedEffect(selectedSchulungId) {
        reloadTeilnehmer()
    }

    val selectedSchulung = remember(schulungen, selectedSchulungId) {
        schulungen.firstOrNull { it.id == selectedSchulungId }
    }
    val schulungenById = remember(schulungen) { schulungen.associateBy { it.id } }
    val teilnehmerByUserId = remember(teilnehmer) { teilnehmer.associateBy { it.id } }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            padding(18.px)
            boxSizing("border-box")
            color(Color.white)
            overflow("hidden")
        }
    }) {
        TabularSplitScreen(
            initialRatio = 0.58f,
            screen1 = {
                TabularScreen(
                    screenKey = if (readOnly) "SchulungsAuskunft" else "Schulungsverwaltung",
                    rollenKey = if (readOnly) "SchulungsAuskunft" else "Schulungsverwaltung",
                    title = if (readOnly) "Schulungsübersicht" else "Schulungsverwaltung",
                    headers = schulungsKopfHeaders,
                    models = schulungen,
                    data = schulungen.map { schulung ->
                        schulung.id to listOf(
                            schulung.id,
                            schulung.bezeichnung.ifBlank { schulung.name },
                            schulung.schulungsModus.ifBlank { "KI" },
                            schulung.typ,
                            if (schulung.beschreibung.orEmpty().isNotBlank()) TABULAR_DOCUMENT_ICON_VALUE else "",
                            schulung.anzahlTeilnehmer,
                            if (schulung.absolviert) "\u2705" else "\u2B1C",
                            schulung.datum,
                            schulung.uhrzeitVon,
                            schulung.uhrzeitBis,
                            schulung.standort,
                            schulung.aktivVon,
                            schulung.aktivBis
                        )
                    },
                    editable = !readOnly,
                    hardReadOnly = readOnly,
                    nonEditableCols = setOf("ID", "Beschreibung", "Anzahl Teilnehmer", "Absolviert"),
                    toggleColumns = setOf("Absolviert"),
                    allowCreate = !readOnly,
                    allowSave = !readOnly,
                    allowDelete = !readOnly,
                    allowImportButton = false,
                    createButtonLabel = "KI-Schulung",
                    onCreateAction = if (readOnly) null else ({ createDialogOpen = true }),
                    extraNavButtons = listOfNotNull(
                        if (readOnly) null else TabularToolbarButton(
                            label = "Bearbeiten",
                            icon = "images/setup.svg",
                            enabled = selectedSchulung != null,
                            onClick = { if (selectedSchulung != null) editDialogOpen = true }
                        ),
                        TabularToolbarButton(
                            label = "KI-Einstellungen",
                            icon = "images/einstellungen.svg",
                            onClick = { settingsDialogOpen = true }
                        )
                    ),
                    onRefresh = {
                        scope.launch {
                            reloadSchulungen()
                            reloadTeilnehmer()
                        }
                    },
                    onSearchWithFilters = { filters ->
                        scope.launch {
                            schulungsFilter = filters
                            schulungen = SchulungApi.searchSchulungen(filters)
                                .sortedBy { it.id.toIntOrNull() ?: 0 }
                        }
                    },
                    onRowClick = { rowId -> selectedSchulungId = rowId },
                    onCellClick = { rowId, colIndex ->
                        selectedSchulungId = rowId
                        if (schulungsKopfHeaders.getOrNull(colIndex) == "Beschreibung") {
                            schulungenById[rowId]
                                ?.takeIf { it.beschreibung.orEmpty().isNotBlank() }
                                ?.let { inhaltPopupSchulung = it }
                        }
                    },
                    onDeleteRows = if (readOnly) null else ({ ids ->
                        ids.forEach { id ->
                            check(SchulungApi.deleteSchulung(id)) {
                                "Schulung $id konnte nicht gelöscht werden."
                            }
                        }
                        if (selectedSchulungId?.let { it in ids } == true) selectedSchulungId = null
                        reloadSchulungen()
                        reloadTeilnehmer()
                    }),
                    onCommit = { rows ->
                        if (readOnly) return@TabularScreen
                        scope.launch {
                            rows.forEach { (rowId, cols) ->
                                val participantIds = SchulungApi.listTeilnehmer(rowId).map { it.id }
                                SchulungApi.saveSchulung(
                                    schulungId = rowId,
                                    name = cols.getOrElse(1) { "" },
                                    modus = cols.getOrElse(2) { "KI" },
                                    typ = cols.getOrElse(3) { "" },
                                    anweisung = schulungenById[rowId]?.beschreibung.orEmpty(),
                                    datum = cols.getOrElse(7) { "" },
                                    uhrzeitVon = cols.getOrElse(8) { "" },
                                    uhrzeitBis = cols.getOrElse(9) { "" },
                                    standort = cols.getOrElse(10) { "" },
                                    aktivVon = cols.getOrElse(11) { "" },
                                    aktivBis = cols.getOrElse(12) { "" }
                                )
                                SchulungApi.planSchulung(
                                    schulungId = rowId,
                                    userIds = participantIds,
                                    modus = cols.getOrElse(2) { "KI" },
                                    datum = cols.getOrElse(7) { "" },
                                    uhrzeitVon = cols.getOrElse(8) { "" },
                                    uhrzeitBis = cols.getOrElse(9) { "" },
                                    standort = cols.getOrElse(10) { "" },
                                    aktivVon = cols.getOrElse(11) { "" },
                                    aktivBis = cols.getOrElse(12) { "" }
                                )
                            }
                            reloadSchulungen()
                            reloadTeilnehmer()
                        }
                    }
                )
            },
            screen2 = selectedSchulung?.let { schulung ->
                {
                    TabularScreen(
                        screenKey = if (readOnly) "SchulungsAuskunft_Teilnehmer" else "Schulungsverwaltung_Teilnehmer",
                        rollenKey = if (readOnly) "SchulungsAuskunft" else "Schulungsverwaltung",
                        title = "Teilnehmer - ${schulung.bezeichnung.ifBlank { schulung.name }}",
                        headers = schulungsTeilnehmerHeaders,
                        data = users.map { user ->
                            val existing = teilnehmerByUserId[user.id]
                            user.id to listOf(
                                if (existing != null) "\u2705" else "\u2B1C",
                                user.id,
                                listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ").ifBlank { user.userName },
                                user.personalnummer,
                                existing?.startSchulung.orEmpty(),
                                existing?.endeSchulung.orEmpty(),
                                if (existing?.teilgenommen == true) "\u2705" else "\u2B1C",
                                existing?.let { ZustandManager.Schulung.textFor(it.zustand) }.orEmpty()
                            )
                        },
                        models = users,
                        editable = false,
                        hardReadOnly = true,
                        nonEditableCols = schulungsTeilnehmerHeaders.toSet() - "Pflicht",
                        toggleColumns = emptySet(),
                        allowCreate = false,
                        allowSave = false,
                        allowDelete = false,
                        allowImportButton = false,
                        includeMetadata = false,
                        onRefresh = {
                            scope.launch {
                                reloadSchulungen()
                                reloadTeilnehmer()
                            }
                        },
                        onSearchWithFilters = { filters ->
                            scope.launch {
                                teilnehmerFilter = filters
                                val filtered = SchulungApi.searchTeilnehmer(schulung.id, filters)
                                users = filtered
                                teilnehmer = filtered.filter { it.schulungId == schulung.id }
                            }
                        },
                        onCommit = { }
                    )
                }
            }
        )
    }

    if (createDialogOpen) {
        SchulungCreateDialog(
            users = users,
            onClose = { createDialogOpen = false },
            onSaved = { saved ->
                scope.launch {
                    selectedSchulungId = saved.id
                    createDialogOpen = false
                    reloadSchulungen()
                    reloadTeilnehmer()
                }
            }
        )
    }

    if (editDialogOpen && selectedSchulung != null) {
        SchulungEditDialog(
            schulung = selectedSchulung,
            users = users,
            selectedUserIds = teilnehmer.map { it.id }.toSet(),
            onClose = { editDialogOpen = false },
            onSaved = {
                scope.launch {
                    editDialogOpen = false
                    selectedSchulungId = null
                    reloadSchulungen()
                    reloadTeilnehmer()
                }
            }
        )
    }

    if (settingsDialogOpen) {
        SchulungKiSettingsDialog(onClose = { settingsDialogOpen = false })
    }

    inhaltPopupSchulung?.let { schulung ->
        SchulungInhaltPopup(
            schulung = schulung,
            onClose = { inhaltPopupSchulung = null }
        )
    }
}

@Composable
private fun SchulungInhaltPopup(schulung: All, onClose: () -> Unit) {
    PopupEngineCard(
        windowKey = "schulung-inhalt-popup-${schulung.id}",
        widthPx = 720,
        heightPx = 560,
        initialX = 420.0,
        initialY = 120.0,
        frameBackground = DashboardTheme.POPUP_BG,
        contentBackground = DashboardTheme.POPUP_BG,
        resizable = true,
        onClose = onClose
    ) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                padding(22.px)
                boxSizing("border-box")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                color(Color.white)
            }
        }) {
            Span({
                style {
                    property("font-size", "20px")
                    property("font-weight", "800")
                }
            }) {
                Text(schulung.bezeichnung.ifBlank { schulung.name.ifBlank { "Schulungsinhalt" } })
            }
            Div({
                style {
                    property("flex", "1")
                    minHeight(0.px)
                    overflow("auto")
                    padding(16.px)
                    property("background", "rgba(255,255,255,0.045)")
                    property("border", "1px solid rgba(255,255,255,0.14)")
                    property("border-radius", "8px")
                    property("white-space", "pre-wrap")
                    property("line-height", "1.45")
                    property("font-size", "14px")
                    color(Color("rgba(255,255,255,0.86)"))
                    boxSizing("border-box")
                }
            }) {
                Text(schulung.beschreibung.orEmpty().ifBlank { "Kein Inhalt hinterlegt." })
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                }
            }) {
                LumaticaButton("Schlie\u00dfen", onClick = onClose, variant = LumaticaButtonVariant.Standard)
            }
        }
    }
}

@Composable
private fun SchulungKiSettingsDialog(onClose: () -> Unit) {
    val refreshVersion = GlobalManager.jsonDomainVersion("schulung")
    val scope = rememberCoroutineScope()
    var settings by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }

    LaunchedEffect(refreshVersion) {
        settings = SchulungApi.loadKiSettings()
        loading = false
    }

    PopupEngineCard(
        windowKey = "schulung-ki-einstellungen",
        widthPx = 760,
        heightPx = 560,
        minWidthPx = 620,
        minHeightPx = 460,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({ style(SchulungDialogStyle) }) {
            H3 { Text("KI-Einstellungen") }
            Span { Text("Diese Einstellungen gelten für alle KI-Schulungen.") }
            TextArea(attrs = {
                value(settings)
                onInput { settings = it.value }
                attr("placeholder", "Zentrale Anweisung für KI-Schulungen")
                style {
                    SchulungTextAreaStyle()
                    property("flex", "1")
                    minHeight(280.px)
                }
            })
            if (error.isNotBlank()) Span { Text(error) }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); justifyContent(JustifyContent.FlexEnd) } }) {
                LumaticaButton("Abbrechen", enabled = !saving, onClick = onClose)
                LumaticaButton(
                    "Speichern",
                    variant = LumaticaButtonVariant.Action,
                    enabled = !loading && !saving && settings.isNotBlank(),
                    onClick = {
                        saving = true
                        error = ""
                        scope.launch {
                            val saved = SchulungApi.saveKiSettings(settings)
                            saving = false
                            if (saved.isBlank()) error = "KI-Einstellungen konnten nicht gespeichert werden."
                            else onClose()
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun SchulungEditDialog(
    schulung: All?,
    users: List<All>,
    selectedUserIds: Set<String>,
    onClose: () -> Unit,
    onSaved: () -> Unit
) {
    val current = schulung ?: return
    val scope = rememberCoroutineScope()
    var name by remember(current.id) { mutableStateOf(current.bezeichnung.ifBlank { current.name }) }
    var typ by remember(current.id) { mutableStateOf(current.typ) }
    var anweisung by remember(current.id) { mutableStateOf(current.beschreibung.orEmpty()) }
    var modus by remember(current.id) { mutableStateOf(current.schulungsModus.ifBlank { "KI" }) }
    var datum by remember(current.id) { mutableStateOf(current.datum) }
    var uhrzeitVon by remember(current.id) { mutableStateOf(current.uhrzeitVon.ifBlank { "09:00" }) }
    var uhrzeitBis by remember(current.id) { mutableStateOf(current.uhrzeitBis.ifBlank { "10:00" }) }
    var standort by remember(current.id) { mutableStateOf(current.standort) }
    var aktivVon by remember(current.id) { mutableStateOf(current.aktivVon.ifBlank { current.datum }) }
    var aktivBis by remember(current.id) { mutableStateOf(current.aktivBis.ifBlank { aktivVon }) }
    var selected by remember(current.id, selectedUserIds) { mutableStateOf(selectedUserIds) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    val isPhysical = modus.equals("PHYSISCH", ignoreCase = true)

    if (isPhysical) {
        PopupEngineCard(
            windowKey = "schulung-bearbeiten-${current.id}",
            widthPx = 620,
            heightPx = 260,
            minWidthPx = 520,
            minHeightPx = 220,
            viewportAnchored = true,
            onClose = onClose
        ) {
            Div({ style(SchulungDialogStyle) }) {
                H3 { Text("Physischer Termin") }
                Span {
                    Text("Physische Schulungstermine werden im Kalender geplant und bearbeitet. Diese Maske ist nur f\u00fcr KI-Schulungen.")
                }
                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                    LumaticaButton("Schlie\u00dfen", variant = LumaticaButtonVariant.Standard, onClick = onClose)
                }
            }
        }
        return
    }

    val visibleUsers = if (isPhysical) users else users.filter { it.zugangDesktop }
    val effectiveSelected = selected.intersect(visibleUsers.map { it.id }.toSet())
    val canSave = name.isNotBlank() && effectiveSelected.isNotEmpty() && if (isPhysical) {
        datum.isNotBlank() && uhrzeitVon.isNotBlank() && uhrzeitBis > uhrzeitVon
    } else {
        aktivVon.isNotBlank() && aktivBis.isNotBlank()
    }

    PopupEngineCard(
        windowKey = "schulung-bearbeiten-${current.id}",
        widthPx = 820,
        heightPx = 680,
        minWidthPx = 680,
        minHeightPx = 520,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({ style(SchulungDialogStyle) }) {
            H3 { Text("Schulung bearbeiten") }
            InputRow("Schulungsname", name) { name = it }
            InputRow("Typ", typ) { typ = it }
            Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) { Text("Schulungstyp: KI-Schulung") }
            DateRangeRows(
                von = aktivVon,
                bis = aktivBis,
                onVon = {
                    aktivVon = it
                    if (aktivBis.isBlank()) aktivBis = it
                },
                onBis = { aktivBis = it }
            )
            TextArea(attrs = {
                value(anweisung)
                onInput { anweisung = it.value }
                attr("placeholder", if (isPhysical) "Beschreibung" else "Schulungsanweisung")
                style(SchulungTextAreaStyle)
            })
            Span { Text("Mitarbeiter: ${effectiveSelected.size}") }
            Div({
                style {
                    minHeight(180.px)
                    overflow("auto")
                    property("border", "1px solid rgba(255,255,255,0.16)")
                    padding(8.px)
                    boxSizing("border-box")
                }
            }) {
                visibleUsers.forEach { user ->
                    CheckboxLine(
                        checked = user.id in selected,
                        label = listOf(user.vorname, user.nachname, user.personalnummer)
                            .filter { it.isNotBlank() }
                            .joinToString(" ")
                            .ifBlank { user.userName.ifBlank { user.id } },
                        onChange = { checked ->
                            selected = if (checked) selected + user.id else selected - user.id
                        }
                    )
                }
            }
            if (error.isNotBlank()) Span { Text(error) }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); justifyContent(JustifyContent.FlexEnd) } }) {
                LumaticaButton("Abbrechen", variant = LumaticaButtonVariant.Standard, enabled = !saving, onClick = onClose)
                LumaticaButton("Speichern", variant = LumaticaButtonVariant.Action, enabled = !saving && canSave, onClick = {
                    saving = true
                    error = ""
                    scope.launch {
                        val saved = SchulungApi.saveSchulung(
                            schulungId = current.id,
                            name = name,
                            typ = typ,
                            anweisung = anweisung,
                            modus = if (isPhysical) "PHYSISCH" else "KI",
                            datum = datum,
                            uhrzeitVon = uhrzeitVon,
                            uhrzeitBis = uhrzeitBis,
                            standort = standort,
                            aktivVon = aktivVon,
                            aktivBis = aktivBis
                        )
                        if (saved == null) {
                            saving = false
                            error = "Schulung konnte nicht gespeichert werden."
                            return@launch
                        }
                        SchulungApi.planSchulung(
                            schulungId = current.id,
                            userIds = effectiveSelected.toList(),
                            modus = if (isPhysical) "PHYSISCH" else "KI",
                            datum = datum,
                            uhrzeitVon = uhrzeitVon,
                            uhrzeitBis = uhrzeitBis,
                            standort = standort,
                            aktivVon = aktivVon,
                            aktivBis = aktivBis
                        )
                        saving = false
                        onSaved()
                    }
                })
            }
        }
    }
}

@Composable
private fun SchulungCreateDialog(
    users: List<All>,
    onClose: () -> Unit,
    onSaved: (All) -> Unit
) {
    val scope = rememberCoroutineScope()
    var name by remember { mutableStateOf("") }
    var typ by remember { mutableStateOf("Pflichtschulung") }
    var anweisung by remember { mutableStateOf("") }
    val modus = "KI"
    val datum = ""
    val uhrzeitVon = ""
    val uhrzeitBis = ""
    val standort = ""
    var aktivVon by remember { mutableStateOf(kalenderTodayIsoDate()) }
    var aktivBis by remember { mutableStateOf(kalenderTodayIsoDate()) }
    var dokumentName by remember { mutableStateOf("schulung.txt") }
    var schulungsMaterial by remember { mutableStateOf("") }
    var uploadedPdfDataUrl by remember { mutableStateOf("") }
    var employeeDialogOpen by remember { mutableStateOf(false) }
    var selected by remember { mutableStateOf<Set<String>>(emptySet()) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    val visibleUsers = users.filter { it.zugangDesktop }
    val effectiveSelected = selected.intersect(visibleUsers.map { it.id }.toSet())
    val hasUnterlagen = schulungsMaterial.isNotBlank() || uploadedPdfDataUrl.isNotBlank()
    val canSave = name.isNotBlank() && typ.isNotBlank() && hasUnterlagen && effectiveSelected.isNotEmpty() &&
        aktivVon.isNotBlank() && aktivBis.isNotBlank()
    val fileInputId = "schulung-create-file-input"

    PopupEngineCard(
        windowKey = "schulung-erzeugen",
        widthPx = 920,
        heightPx = 620,
        minWidthPx = 620,
        minHeightPx = 500,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({ style(SchulungDialogStyle) }) {
            H3(attrs = {
                style {
                    property("text-align", "center")
                    property("margin", "0 0 8px 0")
                }
            }) { Text("Schulung anlegen") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(14.px)
                    alignItems(AlignItems.FlexStart)
                }
            }) {
                Div({ style { property("flex", "1") } }) {
                    InputRow("Bezeichnung", name) { name = it }
                }
                Div({ style { property("width", "260px") } }) {
                    InputRow("Schulungstyp", typ) { typ = it }
                }
            }

            Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) {
                Text("Schulungstyp: KI-Schulung")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(14.px)
                    alignItems(AlignItems.FlexStart)
                }
            }) {
                Div({ style { property("flex", "1") } }) {
                    DateFieldRow("Startet", aktivVon, {
                        aktivVon = it
                        if (aktivBis.isBlank()) aktivBis = it
                    }, "schulung-minikalender-startet")
                }
                Div({ style { property("flex", "1") } }) {
                    DateFieldRow("Endet", aktivBis, { aktivBis = it }, "schulung-minikalender-endet")
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(14.px)
                    alignItems(AlignItems.FlexStart)
                }
            }) {
                Div({ style { property("flex", "1") } }) {
                    Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) {
                        Text("Geben Sie hier Ihre Schulungsunterlagen ein")
                    }
                    TextArea(attrs = {
                        value(schulungsMaterial)
                        onInput {
                            schulungsMaterial = it.value
                            uploadedPdfDataUrl = ""
                            if (dokumentName.isBlank()) dokumentName = "schulung.txt"
                        }
                        attr("placeholder", "Schulungsunterlagen / Anweisung für die KI")
                        style {
                            SchulungTextAreaStyle()
                            minHeight(150.px)
                        }
                    })
                }
                Div({
                    style {
                        property("width", "220px")
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(8.px)
                        property("padding-top", "22px")
                    }
                }) {
                    Input(type = InputType.File) {
                        attr("id", fileInputId)
                        accept(".txt,.pdf,text/plain,application/pdf")
                        style { property("display", "none") }
                        onChange { event ->
                            val file = event.target.files?.item(0) ?: return@onChange
                            dokumentName = file.name
                            scope.launch {
                                val content = readTrainingFile(file)
                                if (file.type == "application/pdf" || file.name.lowercase().endsWith(".pdf")) {
                                    uploadedPdfDataUrl = content
                                    schulungsMaterial = ""
                                } else {
                                    uploadedPdfDataUrl = ""
                                    schulungsMaterial = content
                                }
                            }
                        }
                    }
                    LumaticaButton(
                        label = "Datei ausw\u00e4hlen",
                        variant = LumaticaButtonVariant.Standard,
                        onClick = { document.getElementById(fileInputId).asDynamic().click() }
                    )
                    Span({ style { color(Color("rgba(255,255,255,0.62)")) } }) {
                        Text(dokumentName.takeIf { uploadedPdfDataUrl.isNotBlank() || schulungsMaterial.isNotBlank() } ?: "Keine Datei ausgew\u00e4hlt")
                    }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Span { Text("Mitarbeiter: ${effectiveSelected.size}") }
                LumaticaButton(
                    label = "MA hinzuf\u00fcgen",
                    variant = LumaticaButtonVariant.Standard,
                    onClick = { employeeDialogOpen = true }
                )
            }

            if (error.isNotBlank()) Span { Text(error) }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); justifyContent(JustifyContent.FlexEnd) } }) {
                LumaticaButton("Abbrechen", variant = LumaticaButtonVariant.Standard, enabled = !saving, onClick = onClose)
                LumaticaButton("Speichern", variant = LumaticaButtonVariant.Action, enabled = !saving && canSave, onClick = {
                    saving = true
                    error = ""
                    scope.launch {
                        try {
                            val kiSettings = if (uploadedPdfDataUrl.isBlank()) "" else SchulungApi.loadKiSettings()
                            val pdfDataUrl = if (uploadedPdfDataUrl.isBlank()) "" else buildSchulungPdf(
                                name = name,
                                typ = typ,
                                modus = modus,
                                aktivVon = aktivVon,
                                aktivBis = aktivBis,
                                kiSettings = kiSettings,
                                anweisung = schulungsMaterial,
                                material = uploadedPdfDataUrl
                            )
                            val saved = SchulungApi.createSchulung(
                                name = name,
                                typ = typ,
                                anweisung = schulungsMaterial,
                                dokumentName = schulungsPdfName(dokumentName, name),
                                dokumentPdfDataUrl = pdfDataUrl,
                                modus = modus,
                                datum = datum,
                                uhrzeitVon = uhrzeitVon,
                                uhrzeitBis = uhrzeitBis,
                                standort = standort,
                                aktivVon = aktivVon,
                                aktivBis = aktivBis
                            )
                            if (saved == null) {
                                error = "Schulung konnte nicht gespeichert werden."
                                return@launch
                            }
                            SchulungApi.planSchulung(
                                schulungId = saved.id,
                                userIds = effectiveSelected.toList(),
                                modus = modus,
                                datum = datum,
                                uhrzeitVon = uhrzeitVon,
                                uhrzeitBis = uhrzeitBis,
                                standort = standort,
                                aktivVon = aktivVon,
                                aktivBis = aktivBis
                            )
                            onSaved(saved)
                        } catch (failure: Throwable) {
                            error = failure.message ?: "Schulungs-PDF konnte nicht erzeugt werden."
                        } finally {
                            saving = false
                        }
                    }
                })
            }
        }
    }

    if (employeeDialogOpen) {
        PopupEngineCard(
            windowKey = "schulung-ma-auswahl",
            widthPx = 520,
            heightPx = 560,
            minWidthPx = 420,
            minHeightPx = 420,
            viewportAnchored = true,
            onClose = { employeeDialogOpen = false }
        ) {
            Div({ style(SchulungDialogStyle) }) {
                H3 { Text("Mitarbeiter ausw\u00e4hlen") }
                Div({
                    style {
                        property("flex", "1")
                        minHeight(0.px)
                        overflow("auto")
                        property("border", "1px solid rgba(255,255,255,0.16)")
                        padding(8.px)
                        boxSizing("border-box")
                    }
                }) {
                    visibleUsers.forEach { user ->
                        CheckboxLine(
                            checked = user.id in selected,
                            label = listOf(user.vorname, user.nachname, user.personalnummer)
                                .filter { it.isNotBlank() }
                                .joinToString(" ")
                                .ifBlank { user.userName.ifBlank { user.id } },
                            onChange = { checked ->
                                selected = if (checked) selected + user.id else selected - user.id
                            }
                        )
                    }
                }
                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd) } }) {
                    LumaticaButton(
                        label = "Übernehmen",
                        variant = LumaticaButtonVariant.Action,
                        onClick = { employeeDialogOpen = false }
                    )
                }
            }
        }
    }
}

private suspend fun readTrainingFile(file: File): String =
    kotlinx.coroutines.suspendCancellableCoroutine { continuation ->
        val reader = FileReader()
        reader.onloadend = {
            continuation.resume(reader.result as? String ?: "")
            null
        }
        reader.onerror = {
            continuation.resume("")
            null
        }
        if (file.type == "application/pdf" || file.name.lowercase().endsWith(".pdf")) {
            reader.readAsDataURL(file)
        } else {
            reader.readAsText(file)
        }
    }

private suspend fun buildSchulungPdf(
    name: String,
    typ: String,
    modus: String,
    aktivVon: String,
    aktivBis: String,
    kiSettings: String,
    anweisung: String,
    material: String
): String {
    require(kiSettings.isNotBlank()) { "KI-Einstellungen fehlen." }
    require(material.isNotBlank()) { "Schulungsmaterial fehlt." }
    val pdfLib = js("require('pdf-lib')")
    val pdfDocument = awaitPdfPromise(pdfLib.PDFDocument.create())
    val font = awaitPdfPromise(pdfDocument.embedFont(pdfLib.StandardFonts.Helvetica))

    addSchulungTextPages(
        pdfDocument = pdfDocument,
        font = font,
        text = listOf(
            name.ifBlank { "Schulung" },
            "",
            "Schulungsdaten",
            "---------------",
            "Bezeichnung: ${name.ifBlank { "Schulung" }}",
            "Typ: $typ",
            "Modus: $modus",
            "Aktiv von: $aktivVon",
            "Aktiv bis: $aktivBis"
        ).joinToString("\n")
    )

    addSchulungTextPages(
        pdfDocument = pdfDocument,
        font = font,
        text = kiSettings.trim()
    )

    if (anweisung.isNotBlank()) {
        addSchulungTextPages(
            pdfDocument = pdfDocument,
            font = font,
            text = listOf(
                "Schulungsanweisung",
                "------------------",
                anweisung.trim()
            ).joinToString("\n")
        )
    }

    if (material.startsWith("data:application/pdf", ignoreCase = true)) {
        val source = awaitPdfPromise(pdfLib.PDFDocument.load(material))
        val copiedPages = awaitPdfPromise(pdfDocument.copyPages(source, source.getPageIndices()))
        val length = copiedPages.length as Int
        for (index in 0 until length) {
            pdfDocument.addPage(copiedPages[index])
        }
    } else {
        addSchulungTextPages(
            pdfDocument = pdfDocument,
            font = font,
            text = material.trim()
        )
    }

    val bytes = awaitPdfPromise(pdfDocument.save())
    val binary = StringBuilder(bytes.length as Int)
    for (index in 0 until (bytes.length as Int)) {
        binary.append((bytes[index] as Number).toInt().toChar())
    }
    return "data:application/pdf;base64,${kotlinx.browser.window.btoa(binary.toString())}"
}

private fun addSchulungTextPages(pdfDocument: dynamic, font: dynamic, text: String) {
    val lines = text
        .replace("\r\n", "\n")
        .split("\n")
        .flatMap { line -> wrapSchulungPdfLine(line, 88) }
        .ifEmpty { listOf("") }
    val pageWidth = 595.28
    val pageHeight = 841.89
    val margin = 48.0
    val lineHeight = 15.0
    val linesPerPage = ((pageHeight - margin * 2) / lineHeight).toInt().coerceAtLeast(1)

    lines.chunked(linesPerPage).forEach { pageLines ->
        val page = pdfDocument.addPage(arrayOf(pageWidth, pageHeight))
        pageLines.forEachIndexed { index, line ->
            val options = js("{}")
            options.x = margin
            options.y = pageHeight - margin - index * lineHeight
            options.size = 11
            options.font = font
            page.drawText(line, options)
        }
    }
}

private fun wrapSchulungPdfLine(value: String, maxLength: Int): List<String> {
    if (value.isBlank()) return listOf("")
    val result = mutableListOf<String>()
    var current = ""
    value.split(Regex("\\s+")).forEach { word ->
        if (current.isBlank()) {
            current = word
        } else if (current.length + 1 + word.length <= maxLength) {
            current += " $word"
        } else {
            result += current
            current = word
        }
    }
    if (current.isNotBlank()) result += current
    return result
}

private suspend fun awaitPdfPromise(value: dynamic): dynamic =
    value.unsafeCast<Promise<dynamic>>().await()

private fun schulungsPdfName(originalName: String, schulungsName: String): String {
    val base = schulungsName.ifBlank { originalName.substringBeforeLast(".") }.ifBlank { "schulung" }
    return "${base.replace(Regex("""[^\w.-]+"""), "_")}.pdf"
}

@Composable
internal fun InputRow(label: String, value: String, onChange: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(5.px) } }) {
        Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) { Text(label) }
        Input(type = InputType.Text) {
            value(value)
            onInput { onChange(it.value) }
            style {
                height(38.px)
                padding(0.px, 10.px)
                color(Color.white)
                property("background", "rgba(255,255,255,0.04)")
                property("border", "1px solid rgba(255,255,255,0.16)")
                boxSizing("border-box")
            }
        }
    }
}

@Composable
private fun SchulungsmodusRow(modus: String, onChange: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
        Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) { Text("Schulungsmodus") }
        Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
            LumaticaButton(
                "KI-Schulung",
                variant = if (!modus.equals("PHYSISCH", ignoreCase = true)) LumaticaButtonVariant.Action else LumaticaButtonVariant.Standard,
                heightPx = 36,
                onClick = { onChange("KI") }
            )
            LumaticaButton(
                "Physischer Termin",
                variant = if (modus.equals("PHYSISCH", ignoreCase = true)) LumaticaButtonVariant.Action else LumaticaButtonVariant.Standard,
                heightPx = 36,
                onClick = { onChange("PHYSISCH") }
            )
        }
    }
}

@Composable
private fun DateRangeRows(
    von: String,
    bis: String,
    onVon: (String) -> Unit,
    onBis: (String) -> Unit
) {
    Div({ style { display(DisplayStyle.Flex); gap(12.px) } }) {
        Div({ style { property("flex", "1") } }) {
            DateFieldRow("Aktiv von", von, onVon, "schulung-minikalender-aktiv-von")
        }
        Div({ style { property("flex", "1") } }) {
            DateFieldRow("Aktiv bis", bis, onBis, "schulung-minikalender-aktiv-bis")
        }
    }
}

@Composable
private fun DateTimeRows(
    datum: String,
    onDatum: (String) -> Unit,
    uhrzeitVon: String,
    onUhrzeitVon: (String) -> Unit,
    uhrzeitBis: String,
    onUhrzeitBis: (String) -> Unit
) {
    Div({ style { display(DisplayStyle.Flex); gap(12.px) } }) {
        Div({ style { property("flex", "1") } }) {
            DateFieldRow("Datum", datum, onDatum, "schulung-minikalender-datum")
        }
        Div({ style { property("width", "140px") } }) {
            Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) { Text("Von") }
            Input(InputType.Time, attrs = {
                value(uhrzeitVon)
                onInput { onUhrzeitVon(it.value) }
                style {
                    width(100.percent)
                    height(38.px)
                    padding(0.px, 10.px)
                    color(Color.white)
                    property("background", "rgba(255,255,255,0.04)")
                    property("border", "1px solid rgba(255,255,255,0.16)")
                    boxSizing("border-box")
                }
            })
        }
        Div({ style { property("width", "140px") } }) {
            Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) { Text("Bis") }
            Input(InputType.Time, attrs = {
                value(uhrzeitBis)
                onInput { onUhrzeitBis(it.value) }
                style {
                    width(100.percent)
                    height(38.px)
                    padding(0.px, 10.px)
                    color(Color.white)
                    property("background", "rgba(255,255,255,0.04)")
                    property("border", "1px solid rgba(255,255,255,0.16)")
                    boxSizing("border-box")
                }
            })
        }
    }
}

@Composable
private fun DateFieldRow(
    label: String,
    datum: String,
    onDatum: (String) -> Unit,
    windowKey: String
) {
    var calendarOpen by remember { mutableStateOf(false) }
    Span({ style { color(Color("rgba(255,255,255,0.68)")) } }) { Text(label) }
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        Div({
            style {
                width(100.percent)
                height(38.px)
                padding(0.px, 10.px)
                color(Color.white)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("background", "rgba(255,255,255,0.04)")
                property("border", "1px solid rgba(255,255,255,0.16)")
                boxSizing("border-box")
            }
        }) {
            Text(datum.ifBlank { "Datum auswählen" })
        }
        Button({
            attr("type", "button")
            attr("title", "$label auswählen")
            attr("aria-label", "$label auswählen")
            style {
                width(38.px)
                height(38.px)
                padding(8.px)
                color(Color.white)
                cursor("pointer")
                property("background", "rgba(255,255,255,0.04)")
                property("border", "1px solid rgba(255,255,255,0.16)")
                property("border-radius", "4px")
                boxSizing("border-box")
            }
            onClick { calendarOpen = true }
        }) {
            Img("images/kalender.svg") {
                style {
                    width(20.px)
                    height(20.px)
                    property("filter", "brightness(0) invert(1)")
                }
            }
        }
    }
    if (calendarOpen) {
        PopupEngineCard(
            windowKey = windowKey,
            widthPx = 404,
            heightPx = 470,
            minWidthPx = 404,
            minHeightPx = 470,
            viewportAnchored = true,
            onClose = { calendarOpen = false }
        ) {
            Div({ style { padding(28.px, 16.px, 16.px); boxSizing("border-box") } }) {
                EngineMonthCalendar(
                    selectedDate = datum.ifBlank { kalenderTodayIsoDate() },
                    onSelectDate = {
                        onDatum(it)
                        calendarOpen = false
                    },
                    showFooter = false,
                    useWeekBorderLayout = false,
                    showSelectedDayBorder = true
                )
            }
        }
    }
}

@Composable
private fun CheckboxLine(checked: Boolean, label: String, onChange: (Boolean) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            property("align-items", "center")
            gap(8.px)
            padding(6.px, 0.px)
        }
    }) {
        LumaticaCheckbox(checked = checked, onChange = onChange)
        Span { Text(label) }
    }
}

private val SchulungDialogStyle: StyleScope.() -> Unit = {
    width(100.percent)
    height(100.percent)
    padding(44.px, 22.px, 22.px)
    boxSizing("border-box")
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(12.px)
    color(Color.white)
    overflow("auto")
}

internal val SchulungTextAreaStyle: StyleScope.() -> Unit = {
    width(100.percent)
    minHeight(120.px)
    padding(12.px)
    color(Color.white)
    property("background", "rgba(255,255,255,0.04)")
    property("border", "1px solid rgba(255,255,255,0.16)")
    boxSizing("border-box")
    property("resize", "vertical")
}

private val SchulungInlineInputStyle: StyleScope.() -> Unit = {
    width(100.percent)
    height(38.px)
    padding(0.px, 10.px)
    color(Color.white)
    property("background", "rgba(255,255,255,0.04)")
    property("border", "1px solid rgba(255,255,255,0.16)")
    boxSizing("border-box")
}
